import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

// Components
import KogModalSideComponent from "../../modal/ModalSide";
import ModalCarouselFile from "../fileGrid/modalCarouselFile/modalCarouselFile";
import LoadingComponent from "../loading/loadingComponent";
import CollapseEvidenceByPart from "./collapseEvidenceByPart";
import CollapseFiles from "./collapseFiles";
import CollapseImageCategory from "./collapseImageCategory";
import EvidenceUploader from "./evidenceUploader";

// Utils
import { FileTypeEnum } from "@/main/services/enum/FileTypeEnum";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import {
	getGuidScreenByRoute,
	getTranslateByKey,
	isEmpty,
	isNullUndefined,
	useIsShowControlByElementId,
} from "@/main/services/utils";
import { Controls } from "@/main/services/constants/constantsControl";
import { downloadFile, convertImageToBase64 } from "@/main/services/utilsFile";
import {
	EMPTY_VALUE,
	getMimeTypeByExtension,
	isImageExtension,
	isRelatedToPart,
	mapQuoteImageToGridFile,
	removeBase64Prefix,
} from "./evidenceUtils";

// Api's
import { quoteApi, quoteAttachmentApi, quoteImageApi, quoteItemApi, systemGuideApi } from "@/main/api";

// Models
import { IKogModalProps } from "@/main/@types/interfaceIcons";
import { ImportQuoteFileResponse, PartInfoByQuoteResponse, QuoteImageResponse } from "@/main/models/response";
import { SaveQuoteImagesRequest } from "@/main/models/request";

// Hooks
import useEvidenceAccess from "@/main/hooks/useEvidenceAccess";

// Styles
import * as S from "./styles";
import { Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import QuoteImageUploader from "../quoteImageUploader/quoteImageUploader";

type ActiveCollapseType = "files" | "initial" | "supplementary" | "parts" | null;
type CarouselCategoryType = "initial" | "supplementary" | "evidence";

const INITIAL_INDEX_CAROUSEL = 0;

interface ModalImagesProps extends IKogModalProps {
	canUploadImages: boolean;
	canShowPDF: boolean;
	canDownloadImages?: boolean;
	guidQuote: string;
	guidClaim?: string;
	quoteItemGuid?: string;
	showButtonImagesNewTab?: boolean;
	canRelateToGeneralOrder?: boolean;
}

function ModalImages({
	canUploadImages,
	canShowPDF,
	canDownloadImages = false,
	guidQuote,
	guidClaim,
	quoteItemGuid,
	showButtonImagesNewTab = false,
	canRelateToGeneralOrder = true,
}: ModalImagesProps) {
	// States
	const [carouselIndex, setCarouselIndex] = useState(INITIAL_INDEX_CAROUSEL);
	const [isOpenCarousel, setIsOpenCarousel] = useState<boolean>(false);
	const [carouselFiles, setCarouselFiles] = useState<QuoteImageResponse[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadingCarousel, setIsLoadingCarousel] = useState<boolean>(false);
	const [activePanel, setActivePanel] = useState<ActiveCollapseType>(null);
	const [isLoadingSuppCheck, setIsLoadingSuppCheck] = useState<boolean>(true);
	const [hasSupplementary, setHasSupplementary] = useState<boolean>(false);
	const carouselCategoryRef = useRef<CarouselCategoryType>("initial");
	const [reloadTrigger, setReloadTrigger] = useState(0);
	const [isSavingImage, setIsSavingImage] = useState<boolean>(false);
	const [parts, setParts] = useState<PartInfoByQuoteResponse[]>([]);
	const [quoteFiles, setQuoteFiles] = useState<ImportQuoteFileResponse[]>([]);
	const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
	const [evidences, setEvidences] = useState<QuoteImageResponse[]>([]);
	const [isLoadingEvidences, setIsLoadingEvidences] = useState<boolean>(false);
	const location = useLocation();
	const evidenceAccess = useEvidenceAccess();
	const hasUploadEvidenceControl = useIsShowControlByElementId(Controls.IdUploadEvidence);
	const hasClaim = !isEmpty(guidClaim);
	const isClaimView = hasClaim && isEmpty(guidQuote);
	const claimLevelGuidQuote = hasClaim ? "" : guidQuote;
	const quoteLevelGuidClaim = isClaimView ? String(guidClaim) : "";
	const evidenceQuoteItemGuid = !isEmpty(quoteItemGuid) ? String(quoteItemGuid) : undefined;

	const generalFiles = useMemo(() => quoteFiles.filter((file) => !isRelatedToPart(file)), [quoteFiles]);

	const carouselPageRef = useRef(0);

	const handleToggleCollapse = (panel: ActiveCollapseType, isExpanded: boolean) => {
		if (isExpanded) {
			setActivePanel(panel);
		} else {
			setActivePanel((prevPanel) => (prevPanel === panel ? null : prevPanel));
		}
	};

	const handleLoadMoreCarouselFiles = async (): Promise<void> => {
		if (carouselCategoryRef.current === "evidence") return;

		const isInitialCategory = carouselCategoryRef.current === "initial";

		setIsLoadingCarousel(true);
		carouselPageRef.current += 1;
		const request = {
			guidQuote: isInitialCategory ? claimLevelGuidQuote : guidQuote,
			guidClaim: isInitialCategory ? (guidClaim ?? null) : quoteLevelGuidClaim,
			pageIndex: carouselPageRef.current,
			pageSize: PaginationEnum.PageSize10,
			includeInitialQuoteImages: isInitialCategory,
			includeComplementQuoteImages: carouselCategoryRef.current === "supplementary",
		};

		return fetchDataHistoryData(request)
			.then((response) => {
				const newImages: QuoteImageResponse[] = response.results || [];
				setCarouselFiles((currentFiles) => {
					const loadedGuids = new Set(currentFiles.map((item) => item.guid));
					return [...currentFiles, ...newImages.filter((item) => !loadedGuids.has(item.guid))];
				});
			})
			.finally(() => setIsLoadingCarousel(false));
	};

	function renderCarouselFileDetails(image: QuoteImageResponse) {
		const hasDetails = !!image.username || !!image.dateFormatted || !!image.comment;
		if (!hasDetails) return null;

		return (
			<>
				<span>{`${image.username ?? EMPTY_VALUE} - ${image.dateFormatted ?? EMPTY_VALUE}`}</span>
				<span>{image.comment ?? EMPTY_VALUE}</span>
			</>
		);
	}

	useEffect(() => {
		setIsLoadingSuppCheck(true);
		quoteApi
			.getAllClaimImages({
				guidQuote,
				guidClaim: quoteLevelGuidClaim,
				pageIndex: PaginationEnum.PageIndexIntial,
				pageSize: 1,
				includeInitialQuoteImages: false,
				includeComplementQuoteImages: true,
			})
			.then((response) => {
				const hasSupp = !!response.data.results && response.data.results.length > 0;
				setHasSupplementary(hasSupp);
				setActivePanel(hasSupp ? "supplementary" : "initial");
			})
			.finally(() => {
				setIsLoadingSuppCheck(false);
			});
	}, [guidQuote, quoteLevelGuidClaim]);

	useEffect(() => {
		if (!canShowPDF) return;

		setIsLoadingFiles(true);
		setQuoteFiles([]);

		quoteApi
			.getImportQuoteFile({
				guidQuote: String(claimLevelGuidQuote),
				guidClaim: isNullUndefined(guidClaim) ? "" : guidClaim,
				idFileType: FileTypeEnum.ImportQuote,
				guidScreen: getGuidScreenByRoute(location.pathname),
			})
			.then((response) => setQuoteFiles(response.data.result ?? []))
			.catch(() => setQuoteFiles([]))
			.finally(() => setIsLoadingFiles(false));
	}, [claimLevelGuidQuote, guidClaim, location.pathname, canShowPDF, reloadTrigger]);

	useEffect(() => {
		if (!evidenceAccess.canView) {
			setEvidences([]);
			return;
		}

		setIsLoadingEvidences(true);

		quoteApi
			.getAllClaimImages({
				guidQuote,
				guidClaim: quoteLevelGuidClaim,
				guidScreen: evidenceAccess.guidScreen,
				isEvidence: true,
				quoteItemGuid: evidenceQuoteItemGuid,
			})
			.then((response) => setEvidences(response.data.results ?? []))
			.catch(() => setEvidences([]))
			.finally(() => setIsLoadingEvidences(false));
	}, [
		guidQuote,
		quoteLevelGuidClaim,
		evidenceAccess.guidScreen,
		evidenceAccess.canView,
		evidenceQuoteItemGuid,
		reloadTrigger,
	]);

	useEffect(() => {
		if (!evidenceAccess.canView) return;

		quoteItemApi
			.getPartInfosByQuote({
				guidQuote,
				guidClaim: quoteLevelGuidClaim,
				quoteItemGuid: evidenceQuoteItemGuid,
			})
			.then((response) => setParts(response.data.result ?? []))
			.catch(() => setParts([]));
	}, [guidQuote, quoteLevelGuidClaim, evidenceAccess.canView, evidenceQuoteItemGuid]);

	function fetchDataHistoryData(request: any) {
		return quoteApi.getAllClaimImages(request).then((response) => response.data);
	}

	function reloadImages() {
		setIsOpenCarousel(false);
		setCarouselFiles([]);
		setCarouselIndex(INITIAL_INDEX_CAROUSEL);
		setTotalRecords(0);
		setReloadTrigger((prev) => prev + 1);
	}

	function handleClickPreview(
		index: number,
		data: QuoteImageResponse[],
		total: number,
		category: "initial" | "supplementary",
	) {
		setCarouselFiles(data);
		setTotalRecords(total);
		carouselCategoryRef.current = category;

		carouselPageRef.current = Math.ceil(data.length / PaginationEnum.PageSize10);

		setCarouselIndex(index);
		setIsOpenCarousel(true);
	}

	function handleClickEvidencePreview(index: number, previewEvidences: QuoteImageResponse[]) {
		setCarouselFiles(previewEvidences);
		setTotalRecords(previewEvidences.length);
		carouselCategoryRef.current = "evidence";
		carouselPageRef.current = 0;

		setCarouselIndex(index);
		setIsOpenCarousel(true);
	}

	function handleDeleteResult(isDeleted: boolean, successMessageKey: string) {
		if (!isDeleted) {
			toast.error(getTranslateByKey("information.AnErrorOccurred", true));
			return;
		}

		reloadImages();
		toast.success(getTranslateByKey(successMessageKey));
	}

	function handleDeleteQuoteAttachment(guidQuoteAttachment: string | null) {
		if (isNullUndefined(guidQuoteAttachment)) return;

		quoteAttachmentApi
			.deleteAttachment({
				guidQuoteAttachment,
				guidScreen: evidenceAccess.guidScreen,
			})
			.then((response) => handleDeleteResult(response.data.result, "information.recordRemovedSuccessfully"));
	}

	function handleDeleteEvidence(evidence: QuoteImageResponse) {
		if (evidence.isQuoteAttachment) {
			handleDeleteQuoteAttachment(evidence.guid);
			return;
		}

		quoteApi
			.deleteImageQuote({
				guid: evidence.guid,
				isEvidence: true,
				guidScreen: evidenceAccess.guidScreen,
			})
			.then((response) => handleDeleteResult(response.data.result, "information.imagesDeletedSuccessfully"));
	}

	function handDeleteImage(guid: string) {
		if (isNullUndefined(guid)) return;

		quoteApi
			.deleteImageQuote({ guid })
			.then((response) => handleDeleteResult(response.data.result, "information.imagesDeletedSuccessfully"));
	}

	function handleDownloadFile(guidFileImport: string, fileName: string, fileExtension: string) {
		systemGuideApi.downloadImportFile(guidFileImport).then((response) => {
			const base64Data = response.data.result;

			if (!base64Data) {
				toast.error(getTranslateByKey("information.AnErrorOccurred", true));
				return;
			}

			downloadFile(removeBase64Prefix(base64Data), fileName, getMimeTypeByExtension(fileExtension));
		});
	}

	async function handleDownloadImage(imageUrl: string, imageRecord?: QuoteImageResponse) {
		try {
			if (!imageRecord || !imageRecord.guid) {
				toast.error(getTranslateByKey("information.AnErrorOccurred", true));
				return;
			}

			if (imageRecord.isQuoteAttachment || !isImageExtension(imageRecord.fileExtension ?? "")) {
				handleDownloadFile(imageRecord.guid, imageRecord.fileName, imageRecord.fileExtension);
				return;
			}

			const guidFile = imageRecord.guid;

			const response = await quoteImageApi.getDownloadImage(guidFile);
			let base64Data = response.data.result.value;

			if (!base64Data) {
				toast.error(getTranslateByKey("information.AnErrorOccurred", true));
				return;
			}

			if (base64Data.includes("base64,")) {
				base64Data = base64Data.split("base64,")[1];
			}

			const fileName = imageRecord.fileName || `image-${guidFile.substring(0, 8)}.jpeg`;

			downloadFile(base64Data, fileName, `image/${imageRecord.fileExtension.toLowerCase()}`);
		} catch (error) {
			console.error(error);
		}
	}

	//#endregion
	return (
		<S.Wrapper>
			<S.BoxModalImages>
				{!isLoadingSuppCheck ? (
					<>
						{isSavingImage ? (
							<LoadingComponent />
						) : (
							<>
								{evidenceAccess.canUpload && (
									<EvidenceUploader
										guidQuote={String(guidQuote)}
										guidScreen={evidenceAccess.guidScreen}
										parts={parts}
										canRelateToGeneralOrder={canRelateToGeneralOrder}
										onUploaded={reloadImages}
									/>
								)}
								{canUploadImages && hasUploadEvidenceControl && !evidenceAccess.canUpload && (
									<QuoteImageUploader
										quoteImages={[]}
										isFullScreenDragDrop
										onCustomInsert={async (newFiles) => {
											const base64ImagesArray = await Promise.all(newFiles.map((image) => convertImageToBase64(image)));

											const base64StringsArray = base64ImagesArray.filter(Boolean).map((image) => {
												return {
													imageUpload: `data:image/jpeg;base64,${image.base64Image}`,
													imageName: image.name,
												};
											});
											setIsSavingImage(true);
											const request: SaveQuoteImagesRequest = {
												guidQuote: String(guidQuote),
												imagesRequest: base64StringsArray,
											};
											if (request.imagesRequest.length > 0) {
												quoteApi
													.saveQuoteImages(request)
													.then(() => {
														//reload because it affects ordering
														reloadImages();
														toast.success(getTranslateByKey("information.imagesSavedSuccessfully"));
													})
													.finally(() => setIsSavingImage(false));
											}
										}}
									/>
								)}
								{showButtonImagesNewTab && (
									<Button
										sx={{ textTransform: "none", marginBottom: "16px" }}
										onClick={() => {
											window.open(`/#${location.pathname}${location.search}&imageMode=true`, "_blank");
										}}
									>
										{getTranslateByKey("information.openInNewTab")}
									</Button>
								)}
							</>
						)}
						{canShowPDF && (
							<CollapseFiles
								files={generalFiles}
								isLoading={isLoadingFiles}
								expanded={activePanel === "files"}
								onToggle={(isExpanded) => handleToggleCollapse("files", isExpanded)}
								onDeleteFile={handleDeleteQuoteAttachment}
								onDownloadFile={handleDownloadFile}
							/>
						)}

						<CollapseImageCategory
							title={getTranslateByKey("information.initialImages")}
							categoryType="initial"
							guidQuote={claimLevelGuidQuote}
							guidClaim={guidClaim}
							expanded={activePanel === "initial"}
							onToggle={(isExpanded) => handleToggleCollapse("initial", isExpanded)}
							handleDownloadImage={handleDownloadImage}
							canDownloadImages={canDownloadImages}
							onPreview={(idx, data, total) => handleClickPreview(idx, data, total, "initial")}
							collapseMaxHeight="100%"
							collapseOverflow="hidden"
							handDeleteImage={handDeleteImage}
							reloadTrigger={reloadTrigger}
						/>

						{hasSupplementary && (
							<CollapseImageCategory
								title={getTranslateByKey("information.supplementaryImages")}
								categoryType="supplementary"
								guidQuote={guidQuote}
								guidClaim={quoteLevelGuidClaim}
								expanded={activePanel === "supplementary"}
								onToggle={(isExpanded) => handleToggleCollapse("supplementary", isExpanded)}
								handleDownloadImage={handleDownloadImage}
								canDownloadImages={canDownloadImages}
								onPreview={(idx, data, total) => handleClickPreview(idx, data, total, "supplementary")}
								collapseMaxHeight="100%"
								collapseOverflow="hidden"
								handDeleteImage={handDeleteImage}
								reloadTrigger={reloadTrigger}
							/>
						)}

						{evidenceAccess.canView && (
							<CollapseEvidenceByPart
								evidences={evidences}
								isLoading={isLoadingEvidences}
								canDownloadImages={canDownloadImages}
								expanded={activePanel === "parts"}
								reloadTrigger={reloadTrigger}
								onToggle={(isExpanded) => handleToggleCollapse("parts", isExpanded)}
								onDeleteEvidence={handleDeleteEvidence}
								onDownloadEvidence={handleDownloadImage}
								onPreview={handleClickEvidencePreview}
							/>
						)}

						{carouselFiles.length > 0 && (
							<ModalCarouselFile<QuoteImageResponse>
								dataGrid={carouselFiles}
								mapToGridFile={mapQuoteImageToGridFile}
								open={isOpenCarousel}
								setOpen={setIsOpenCarousel}
								totalRecords={totalRecords}
								isLoading={isLoadingCarousel}
								currentIndexCarousel={carouselIndex}
								setCurrentIndexCarousel={setCarouselIndex}
								getNextPageDataSearch={handleLoadMoreCarouselFiles}
								canDownload={canDownloadImages}
								handleDownload={handleDownloadImage}
								renderFileDetails={renderCarouselFileDetails}
							/>
						)}
					</>
				) : (
					<LoadingComponent />
				)}
			</S.BoxModalImages>
		</S.Wrapper>
	);
}

function WrapperModalImages(props: ModalImagesProps) {
	const titleModal = `${getTranslateByKey("information.images")} / ${getTranslateByKey("information.files")}`;

	return (
		<KogModalSideComponent title={titleModal} open={props.isOpenModal} closeModal={props.closeModal}>
			{props.isOpenModal && <ModalImages {...props} />}
		</KogModalSideComponent>
	);
}
export default WrapperModalImages;
